/**
 * ★ [E-72, sobre o E-64] Os ARQUIVOS que o agente entregou na conversa —
 * listar e baixar.
 *
 * **O defeito que esta classe fecha:** a porta pública do backend existia,
 * estava testada e ninguém a chamava. Medido em 2026-09-20, com o arquivo do
 * run real `9bc2fb3d` baixando byte a byte pelo HTTP:
 *
 *     grep -rn "intelligence-v2/conversas" aawz-web-frontend/src  -> vazio
 *
 * Ou seja: o agente gerava a planilha, o backend a entregava com recibo, e o
 * consultor NÃO a recebia — porque nenhuma tela pedia.
 *
 * **O envelope do download já é o que `downloadBase64()` consome.** O backend
 * responde por `FileHandler.send_file_to_front_base_64()`
 * (`resources/resource_artefatos_conversa.py`), que devolve
 * `value: {base64, name, extension}` — o MESMO shape de outros 90+ endpoints
 * de arquivo da plataforma. Por isso aqui não há parse nem montagem de Blob:
 * o util de `@utils/Download` é o dono dessa conversão (ele corrige base64url,
 * recusa conteúdo vazio e usa Blob em vez de `data:` URI, que travava em
 * arquivo grande).
 *
 * ⚠️ **`manifesto` não é entregável.** O backend já o exclui da lista e
 * devolve 404 no download (`TIPOS_ENTREGAVEIS`), porque é o snapshot interno
 * que restaura o workspace entre turnos. Quem desenha o cartão repete o
 * filtro, porque o canal SSE AO VIVO não passa por essa guarda — ver
 * `components/chat-message-artefatos/`.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { requireId } from '@modules/aawz-intelligence-v2/api/guardas'
import { intelligenceV2 } from '@modules/aawz-intelligence-v2/constants/URL'
import { downloadBase64 } from '@utils/Download'

// Mensagens COM a próxima ação (regra "mensagem de erro sempre inclui a
// solução"): o consultor precisa saber o que fazer, não só que falhou.
const ERRO_LISTAR = 'Não foi possível carregar os arquivos desta conversa. Tente novamente mais tarde.'
const ERRO_BAIXAR = 'Não foi possível baixar o arquivo. Tente novamente mais tarde.'

class APIArtefatosConversa extends APITemplate {
  /**
   * `GET /conversas/{id}/artefatos` — os arquivos ENTREGUES na conversa.
   *
   * É o que reidrata o cartão depois do F5: o evento SSE `arquivo` é
   * transiente e morre com o turno (mesmo modo de falha do MUST-1 do cartão
   * de fonte web, que já custou uma correção nesta base).
   *
   * @param {Object} params
   * @param {String} params.idConversa Conversa aberta.
   * @returns {Promise<Object>} `{status, message, value: {artefatos: []}}`.
   */
  async listar({ idConversa } = {}) {
    const guard = requireId(idConversa)
    if (guard) return guard

    const r = await con.get(intelligenceV2.conversaArtefatos(idConversa))
    return r || this.buildErrorResponse({ message: ERRO_LISTAR })
  }

  /**
   * `GET /conversas/{id}/artefatos/{id_objeto}` — baixa UM arquivo e dispara
   * o download no navegador.
   *
   * ⚠️ **200 com `base64` ausente é reportado como 500.** O feedback pinta
   * 2xx de verde (`state/modules/feedback/mutations.js`), então um erro que
   * carregasse o 200 da resposta apareceria ao consultor como sucesso — com
   * nenhum arquivo no disco. Mesma guarda de `services/DownloadFile.js`.
   *
   * @param {Object} params
   * @param {String} params.idConversa Conversa dona do artefato.
   * @param {String} params.idObjeto `id_objeto` do recibo de entrega.
   * @returns {Promise<Object>} `{status, message, value}` — 200 só quando o
   *   arquivo de fato foi para o disco.
   */
  async baixar({ idConversa, idObjeto } = {}) {
    const guardConversa = requireId(idConversa)
    if (guardConversa) return guardConversa
    const guardObjeto = requireId(idObjeto)
    if (guardObjeto) return guardObjeto

    const r = await con.get(intelligenceV2.conversaArtefato(idConversa, idObjeto))

    if (!r || r.status !== 200 || !r.value || !r.value.base64) {
      const statusOrigem = (r && r.status) || 500
      return this.buildErrorResponse({
        message: (r && r.message) || ERRO_BAIXAR,
        status: statusOrigem >= 200 && statusOrigem < 300 ? 500 : statusOrigem,
        value: null,
      })
    }

    // `name`/`extension` vêm do backend, que os separa do nome do recibo
    // (`atachment_filename.rsplit('.', 1)`) — é de lá que sai o MIME certo no
    // `downloadBase64`, mesmo quando o `content_type` do objeto é
    // `application/octet-stream` (medido no run real: a planilha .xlsx é
    // gravada assim).
    downloadBase64(r.value.base64, r.value.name, r.value.extension)

    return this.buildSuccessResponse({ value: true, message: 'Arquivo baixado com sucesso' })
  }
}

export const apiArtefatosConversa = new APIArtefatosConversa()
export { ERRO_BAIXAR, ERRO_LISTAR }
export default APIArtefatosConversa
